import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Wifi, WifiOff, Volume2, VolumeX, Coffee, UtensilsCrossed, LayoutGrid } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { useActiveOrders, Order } from '../../api/queries/order.queries';
import { useBranchStore } from '../../stores/branch.store';
import { useKDSSSE } from '../../hooks/useSSE';
import OrderCard from './components/OrderCard';

// Visibility timer for completed orders (10 minutes)
const COMPLETED_VISIBILITY_MS = 10 * 60 * 1000;

// Audio announcement using Web Speech API
function useAudioAnnouncement(enabled: boolean, language: string) {
  const synthRef = useRef<SpeechSynthesis | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      synthRef.current = window.speechSynthesis;
      // Fallback audio chime
      audioRef.current = new Audio('/chime.mp3');
    }
  }, []);

  const announce = useCallback((order: Order) => {
    if (!enabled) return;

    const displayNumber = order.dailyNumber || order.orderNumber;
    const items = order.items.map(i => i.menuItemName).join(', ');

    // Build announcement text
    const text = language === 'th'
      ? `ออเดอร์หมายเลข ${displayNumber}: ${items}`
      : `Order ${displayNumber}: ${items}`;

    // Try Web Speech API
    if (synthRef.current && 'speechSynthesis' in window) {
      try {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = language === 'th' ? 'th-TH' : 'en-US';
        utterance.rate = 0.9;
        synthRef.current.speak(utterance);
        return;
      } catch (e) {
        console.warn('Speech synthesis failed:', e);
      }
    }

    // Fallback to audio chime
    if (audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [enabled, language]);

  return { announce };
}

type FilterTab = 'all' | 'drinks' | 'food';

function KDSPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const selectedBranchId = useBranchStore((state) => state.selectedBranchId);
  const [_useRealtime] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [completedOrders, setCompletedOrders] = useState<Order[]>([]);
  const announcedOrdersRef = useRef<Set<string>>(new Set());

  // Use selected branch for HQ users, otherwise user's branch
  const effectiveBranchId = selectedBranchId || user?.branchId;

  const { data: orders, isLoading, refetch } = useActiveOrders(effectiveBranchId, true);

  // Audio announcement hook
  const { announce } = useAudioAnnouncement(audioEnabled, i18n.language);

  // Real-time SSE updates
  const sseStatus = useKDSSSE(
    _useRealtime ? effectiveBranchId : undefined,
    useCallback(
      (data: any) => {
        console.log('KDS real-time update:', data);
        refetch();
      },
      [refetch]
    )
  );

  // Handle new order announcements
  const handleNewOrder = useCallback((order: Order) => {
    if (!announcedOrdersRef.current.has(order._id)) {
      announcedOrdersRef.current.add(order._id);
      announce(order);
    }
  }, [announce]);

  // Track completed orders with visibility timer
  useEffect(() => {
    if (!orders) return;

    const filterCompletedOrders = () => {
      const completed = orders.filter(o =>
        o.status === 'completed' || o.status === 'picked_up'
      );

      // Filter out orders older than visibility threshold
      const now = Date.now();
      const visible = completed.filter(o => {
        const completedAt = o.completedAt ? new Date(o.completedAt).getTime() : now;
        return now - completedAt < COMPLETED_VISIBILITY_MS;
      });

      setCompletedOrders(visible);
    };

    // Initial filter
    filterCompletedOrders();

    // Set up periodic cleanup every 30 seconds to auto-hide old completed orders
    const cleanupInterval = setInterval(filterCompletedOrders, 30000);

    return () => clearInterval(cleanupInterval);
  }, [orders]);

  // Filter orders based on station/category
  const filterOrdersByStation = useCallback((orderList: Order[]) => {
    if (filterTab === 'all') return orderList;

    return orderList.filter(order => {
      const hasDrinks = order.items.some(item =>
        item.stationId === 'drinks' || !item.stationId // Default to drinks
      );
      const hasFood = order.items.some(item =>
        item.stationId === 'food'
      );

      if (filterTab === 'drinks') return hasDrinks;
      if (filterTab === 'food') return hasFood;
      return true;
    });
  }, [filterTab]);

  // Group orders by status
  const allPendingOrders = orders?.filter((o) => o.status === 'new') || [];
  const allPreparingOrders = orders?.filter((o) => o.status === 'in_progress') || [];
  const allReadyOrders = orders?.filter((o) => o.status === 'ready') || [];

  const pendingOrders = filterOrdersByStation(allPendingOrders);
  const preparingOrders = filterOrdersByStation(allPreparingOrders);
  const readyOrders = filterOrdersByStation(allReadyOrders);

  // Calculate badge counts for other categories when filtered
  const getOtherCategoryCount = () => {
    if (filterTab === 'all') return 0;
    const otherTab = filterTab === 'drinks' ? 'food' : 'drinks';
    const allOrders = [...allPendingOrders, ...allPreparingOrders, ...allReadyOrders];
    return allOrders.filter(order =>
      order.items.some(item => item.stationId === otherTab)
    ).length;
  };

  const otherCategoryCount = getOtherCategoryCount();

  const columns = [
    {
      title: t('kds.newOrders'),
      status: 'new',
      orders: pendingOrders,
      color: 'bg-blue-50 dark:bg-blue-950/20',
      headerColor: 'bg-blue-500',
    },
    {
      title: t('kds.inProgress'),
      status: 'in_progress',
      orders: preparingOrders,
      color: 'bg-orange-50 dark:bg-orange-950/20',
      headerColor: 'bg-orange-500',
    },
    {
      title: t('kds.ready'),
      status: 'ready',
      orders: readyOrders,
      color: 'bg-green-50 dark:bg-green-950/20',
      headerColor: 'bg-green-500',
    },
    // Completed orders column (auto-hide after 5 minutes)
    ...(completedOrders.length > 0 ? [{
      title: t('kds.pickedUp'),
      status: 'completed',
      orders: completedOrders,
      color: 'bg-gray-50 dark:bg-gray-950/20',
      headerColor: 'bg-gray-500',
    }] : []),
  ];

  const filterTabs = [
    { id: 'all' as FilterTab, label: 'All', icon: LayoutGrid },
    { id: 'drinks' as FilterTab, label: 'Drinks', icon: Coffee },
    { id: 'food' as FilterTab, label: 'Food', icon: UtensilsCrossed },
  ];

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('kds.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {orders?.filter(o => ['new', 'in_progress', 'ready'].includes(o.status)).length || 0} {t('kds.activeOrders') || 'active orders'}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Filter Tabs */}
            <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
              {filterTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = filterTab === tab.id;
                const showBadge = !isActive && tab.id !== 'all' && filterTab !== 'all' &&
                  ((tab.id === 'drinks' && filterTab === 'food') || (tab.id === 'food' && filterTab === 'drinks'));

                return (
                  <button
                    key={tab.id}
                    onClick={() => setFilterTab(tab.id)}
                    className={`relative flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                    {showBadge && otherCategoryCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                        {otherCategoryCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="h-6 border-l border-border" />

            {/* Audio Toggle */}
            <button
              onClick={() => setAudioEnabled(!audioEnabled)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
                audioEnabled ? 'bg-primary/10 text-primary border-primary' : 'bg-muted text-muted-foreground'
              }`}
              title={audioEnabled ? 'Disable audio announcements' : 'Enable audio announcements'}
            >
              {audioEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              <span className="text-sm">{audioEnabled ? 'Audio On' : 'Audio Off'}</span>
            </button>

            {/* Real-time connection status */}
            <div className="flex items-center gap-2">
              {sseStatus.isConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-green-500" />
                  <span className="text-xs text-green-600">Real-time</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-500">Polling</span>
                </>
              )}
            </div>

            {/* Manual refresh button */}
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="rounded-lg border bg-background px-4 py-2 hover:bg-secondary transition-colors tap-highlight-none touch-target disabled:opacity-50"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Kanban Columns */}
      <div className="flex-1 overflow-hidden">
        <div className={`grid gap-4 h-full p-4 ${
          columns.length === 4 ? 'grid-cols-4' : 'grid-cols-3'
        }`}>
          {columns.map((column) => (
            <div key={column.status} className="flex flex-col min-h-0">
              {/* Column Header */}
              <div className={`rounded-t-lg ${column.headerColor} text-white p-3 flex items-center justify-between`}>
                <h2 className="font-bold">{column.title}</h2>
                <span className="bg-white/20 rounded-full px-3 py-1 text-sm font-medium">
                  {column.orders.length}
                </span>
              </div>

              {/* Column Content */}
              <div className={`flex-1 rounded-b-lg ${column.color} p-3 overflow-y-auto space-y-3`}>
                {column.orders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <p className="text-sm">{t('kds.noOrders') || 'No orders'}</p>
                  </div>
                ) : (
                  column.orders.map((order) => (
                    <OrderCard
                      key={order._id}
                      order={order}
                      onNewOrder={column.status === 'new' ? handleNewOrder : undefined}
                    />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Stats */}
      <div className="border-t bg-card p-3">
        <div className="grid grid-cols-4 gap-4 text-center text-sm">
          <div>
            <div className="font-bold text-blue-500 text-lg">{pendingOrders.length}</div>
            <div className="text-muted-foreground">{t('kds.newOrders')}</div>
          </div>
          <div>
            <div className="font-bold text-orange-500 text-lg">{preparingOrders.length}</div>
            <div className="text-muted-foreground">{t('kds.inProgress')}</div>
          </div>
          <div>
            <div className="font-bold text-green-500 text-lg">{readyOrders.length}</div>
            <div className="text-muted-foreground">{t('kds.ready')}</div>
          </div>
          <div>
            <div className="font-bold text-gray-500 text-lg">{completedOrders.length}</div>
            <div className="text-muted-foreground">{t('kds.pickedUp')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KDSPage;
